import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import Mandala from "../components/decor/Mandala";
import { useLang } from "../i18n/LanguageContext";

const BHOOTHARAJA_IMG =
  "https://customer-assets-jt897jd0.emergentagent.net/job_vadiraja-legacy/artifacts/fcq2ctsr_SriBhootharajaru.jpeg";

const CONTENT = {
  en: {
    heroEye: "About Sri Bhootharaja",
    heroTitle: "The Sacred Story of Bhūtarāja",
    heroSubtitle: "Bhūtarāja, the devoted servant of Śrī Vādirāja and protector of devotees.",
    ctaBtn: "Plan Your Visit to Sri Sode Kshetra",
    sections: [
      {
        title: "The Sacred Story of Bhūtarāja",
        paragraphs: [
          "A great devotee and scholar named Nārāyaṇācārya lived in a village called Nārala. He was a devoted disciple of Śrī Vādirāja Tīrtha.",
          "Once, Śrī Vādirāja went to Vijayanagara. On that occasion, He was observing Sādhana Dvādaśī and had been engaged in japa and pārāyaṇa from Ekādaśī itself. On the morning of Dvādaśī, Śrī Vādirāja accepted bhikṣā without performing His āhnika first.",
          "Nārāyaṇācārya noticed this and, wishing to test his Guru, followed Śrī Vādirāja. When they reached the banks of the Tuṅgabhadrā, Śrī Vādirāja ate the food He had received as bhikṣā. He then took the food out again and placed the various items in their proper order. After performing His āhnika, He once again partook of the food.",
          "Śrī Vādirāja understood that His disciple had been testing Him. He realized that Nārāyaṇācārya had committed the mistake of testing his Guru and therefore pronounced upon him the words:",
        ],
        quote: ["May you become a Brahmarākṣasa."],
        paragraphsAfter: [
          "However, this was not a newly created curse. The origin of this curse goes back to the time of Śrī Rāma.",
        ],
      },
      {
        title: "The Earlier Curse",
        paragraphs: [
          "After Śrī Rāma had vanquished Rāvaṇa, He wished to test the understanding of His attendants. He asked them:",
        ],
        quote: [
          "I have slain Rāvaṇa. Rāvaṇa was a Brāhmaṇa. Since I have slain a Brāhmaṇa, I have incurred the sin of Brahmahatyā. Is there anyone who can remove this sin of Brahmahatyā from me?",
        ],
        paragraphsAfter: [
          "In reality, Śrī Rāma had no such sin. He was testing those around Him.",
          "One of the attendants replied, “I shall remove it.” By saying this, he revealed that he believed that Śrī Rāma had indeed incurred the sin of Brahmahatyā.",
          "Śrī Rāma then said:",
        ],
        secondQuote: [
          "Since you have understood that I have incurred Brahmahatyā, may you become a Brahmarākṣasa.",
        ],
        closingParagraphs: [
          "That very curse was now invoked by Śrī Vādirāja upon Nārāyaṇācārya.",
          "Yet Nārāyaṇācārya was no ordinary soul. He was destined to become Bhāvirudra, one who would attain the position of Rudra in the future.",
          "Nārāyaṇācārya then surrendered himself to Śrī Vādirāja and asked:",
        ],
        thirdQuote: ["Swāmī, when will I be freed from this curse?"],
        finalParagraphs: ["Śrī Vādirāja replied:"],
        fourthQuote: [
          "When you become a Brahmarākṣasa, ask everyone this question: ‘Ākāmavai ko na snātaḥ?’ Whoever gives the correct answer to this question will liberate you from the curse.",
        ],
        tailParagraphs: [
          "Accordingly, Nārāyaṇācārya became a Brahmarākṣasa and began asking this question to everyone he encountered. However, no one was able to provide the correct answer.",
          "Eventually, the Brahmarākṣasa reached Badarīkṣetra, where he encountered Śrī Vādirāja. Without recognizing that He was the very Guru who had pronounced the curse upon him, he asked Śrī Vādirāja the same question:",
        ],
        fifthQuote: ["Ākāmavai ko na snātaḥ?"],
        answerIntro: ["Śrī Vādirāja then gave the answer.", "He explained that:"],
        bullets: [
          { prefix: "Ā", middle: " refers to ", suffix: "Āṣāḍha" },
          { prefix: "Ka", middle: " refers to ", suffix: "Kārtika" },
          { prefix: "Ma", middle: " refers to ", suffix: "Māgha" },
          { prefix: "Vai", middle: " refers to ", suffix: "Vaiśākha" },
        ],
        answerParagraphs: [
          "Bathing during these months is considered especially meritorious. But, having become a Brahmarākṣasa, Nārāyaṇācārya had not performed such sacred baths.",
          "Thus, “Ākāmavai ko na snātaḥ?” means:",
        ],
        sixthQuote: ["Who has not bathed during these months?"],
        answerTail: ["The answer was:"],
        seventhQuote: ["Tvam na snātaḥ” — “You have not bathed."],
        finalTail: [
          "As soon as the correct answer was given, Nārāyaṇācārya was released from the curse.",
          "From that day onwards, he became a devoted servant of Śrī Vādirāja and carried His pallakki (palanquin). Śrī Vādirāja affectionately called him Bhūtarāja, meaning the king of the Bhūta-gaṇas.",
          "Thus began the sacred association of Bhūtarāja with Śrī Vādirāja.",
        ],
      },
      {
        title: "Bhūtarāja at Sode",
        paragraphs: [
          "Even today, devotees undertaking a pilgrimage to Sode traditionally seek the darśana of Bhūtarāja before proceeding further. Devotees offer two coconuts before Bhūtarāja and then continue their pilgrimage.",
          "The two coconuts traditionally signify:",
        ],
        bullets: [
          "Iṣṭa-prāpti — attainment of the desired",
          "Aniṣṭa-nivṛtti — removal of the undesirable",
        ],
        paragraphsAfter: [
          "Bhūtarāja is revered as the devoted servant of Śrī Vādirāja and as a protector who removes obstacles and protects devotees.",
          "The traditional prayer offered to Bhūtarāja is:",
        ],
        prayer: [
          "Bhūtarāja namastubhyaṁ",
          "Vādirāja-guroḥ priya",
          "Vidrāvaya sarva-bādhāṁ",
          "Naḥ sadā tvaṁ rakṣaṇaṁ kuru.",
        ],
        meaningTitle: "Meaning",
        meaning: [
          "O Bhūtarāja, I offer my salutations to you,",
          "the beloved servant of Guru Vādirāja.",
          "Drive away all afflictions and obstacles,",
          "and always protect us.",
        ],
      },
    ],
  },
  kn: {
    heroEye: "ಶ್ರೀ ಭೂತರಾಜರ ಬಗ್ಗೆ",
    heroTitle: "ಶ್ರೀ ಭೂತರಾಜರ ಪವಿತ್ರ ಕಥೆ",
    heroSubtitle: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಪರಮ ಭಕ್ತ ಸೇವಕರು ಹಾಗೂ ಭಕ್ತರನ್ನು ರಕ್ಷಿಸುವ ರಕ್ಷಕರು.",
    ctaBtn: "ಶ್ರೀ ಸೋದೆ ಕ್ಷೇತ್ರಕ್ಕೆ ನಿಮ್ಮ ಭೇಟಿ ಯೋಜಿಸಿ",
    sections: [
      {
        title: "ಶ್ರೀ ಭೂತರಾಜರ ಪವಿತ್ರ ಕಥೆ",
        paragraphs: [
          "ನಾರಳ ಎಂಬ ಗ್ರಾಮದಲ್ಲಿ ನಾರಾಯಣಾಚಾರ್ಯ ಎಂಬ ಮಹಾನ್ ಭಕ್ತರು ಹಾಗೂ ವಿದ್ವಾಂಸರು ವಾಸಿಸುತ್ತಿದ್ದರು. ಅವರು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಪರಮ ಶಿಷ್ಯರಾಗಿದ್ದರು.",
          "ಒಮ್ಮೆ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ವಿಜಯನಗರಕ್ಕೆ ತೆರಳಿದರು. ಆ ಸಂದರ್ಭದಲ್ಲಿ ಅವರು ಸಾಧನ ದ್ವಾದಶಿಯನ್ನು ಆಚರಿಸುತ್ತಿದ್ದು, ಏಕಾದಶಿಯಿಂದಲೇ ಜಪ ಮತ್ತು ಪಾರಾಯಣದಲ್ಲಿ ನಿರತರಾಗಿದ್ದರು. ದ್ವಾದಶಿಯ ಬೆಳಿಗ್ಗೆ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ತಮ್ಮ ಆಹ್ನಿಕವನ್ನು ನೆರವೇರಿಸುವ ಮೊದಲು ಭಿಕ್ಷೆಯನ್ನು ಸ್ವೀಕರಿಸಿದರು.",
          "ಇದನ್ನು ಗಮನಿಸಿದ ನಾರಾಯಣಾಚಾರ್ಯರು ತಮ್ಮ ಗುರುಗಳನ್ನು ಪರೀಕ್ಷಿಸಬೇಕೆಂಬ ಉದ್ದೇಶದಿಂದ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರನ್ನು ಹಿಂಬಾಲಿಸಿದರು. ಅವರು ತುಂಗಭದ್ರಾ ನದಿಯ ದಡವನ್ನು ತಲುಪಿದಾಗ, ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಭಿಕ್ಷೆಯಾಗಿ ಸ್ವೀಕರಿಸಿದ್ದ ಆಹಾರವನ್ನು ಸೇವಿಸಿದರು. ನಂತರ ಅವರು ಆ ಆಹಾರವನ್ನು ಮತ್ತೆ ಹೊರತೆಗೆದು, ಅದರಲ್ಲಿದ್ದ ವಿವಿಧ ಪದಾರ್ಥಗಳನ್ನು ಅವುಗಳ ಕ್ರಮದಲ್ಲಿ ಪ್ರತ್ಯೇಕವಾಗಿ ಇಟ್ಟರು. ತಮ್ಮ ಆಹ್ನಿಕವನ್ನು ನೆರವೇರಿಸಿದ ಬಳಿಕ, ಅವರು ಆ ಆಹಾರವನ್ನು ಮತ್ತೊಮ್ಮೆ ಸೇವಿಸಿದರು.",
          "ತಮ್ಮ ಶಿಷ್ಯನು ತಮ್ಮನ್ನು ಪರೀಕ್ಷಿಸುತ್ತಿದ್ದಾನೆ ಎಂಬುದನ್ನು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಅರಿತುಕೊಂಡರು. ತನ್ನ ಗುರುಗಳನ್ನು ಪರೀಕ್ಷಿಸುವ ತಪ್ಪನ್ನು ನಾರಾಯಣಾಚಾರ್ಯರು ಮಾಡಿದ್ದಾರೆ ಎಂಬುದನ್ನು ತಿಳಿದು, ಅವರು ನಾರಾಯಣಾಚಾರ್ಯರಿಗೆ ಹೀಗೆ ಶಾಪ ನೀಡಿದರು:",
        ],
        quote: ["ನೀನು ಬ್ರಹ್ಮರಾಕ್ಷಸನಾಗು."],
        paragraphsAfter: [
          "ಆದರೆ ಇದು ಹೊಸದಾಗಿ ನೀಡಲಾದ ಶಾಪವಾಗಿರಲಿಲ್ಲ. ಈ ಶಾಪದ ಮೂಲವು ಶ್ರೀ ರಾಮನ ಕಾಲಕ್ಕೆ ಹೋಗುತ್ತದೆ.",
        ],
      },
      {
        title: "ಹಿಂದಿನ ಶಾಪ",
        paragraphs: [
          "ಶ್ರೀ ರಾಮನು ರಾವಣನನ್ನು ಸಂಹರಿಸಿದ ನಂತರ, ತನ್ನ ಪರಿವಾರದವರ ಜ್ಞಾನವನ್ನು ಪರೀಕ್ಷಿಸಲು ಬಯಸಿದನು. ಅವರು ಹೀಗೆ ಕೇಳಿದರು:",
        ],
        quote: [
          "ನಾನು ರಾವಣನನ್ನು ಸಂಹರಿಸಿದ್ದೇನೆ. ರಾವಣನು ಬ್ರಾಹ್ಮಣನಾಗಿದ್ದನು. ನಾನು ಒಬ್ಬ ಬ್ರಾಹ್ಮಣನನ್ನು ಸಂಹರಿಸಿರುವುದರಿಂದ ನನಗೆ ಬ್ರಹ್ಮಹತ್ಯಾ ದೋಷವುಂಟಾಗಿದೆ. ಈ ಬ್ರಹ್ಮಹತ್ಯಾ ದೋಷವನ್ನು ನನ್ನಿಂದ ಪರಿಹರಿಸಲು ಯಾರಾದರೂ ಇದ್ದಾರೆಯೇ?",
        ],
        paragraphsAfter: [
          "ವಾಸ್ತವವಾಗಿ, ಶ್ರೀ ರಾಮನಿಗೆ ಅಂತಹ ಯಾವುದೇ ದೋಷವಿರಲಿಲ್ಲ. ಅವರು ತಮ್ಮ ಸುತ್ತಲಿದ್ದವರನ್ನು ಪರೀಕ್ಷಿಸುತ್ತಿದ್ದರು.",
          "ಆಗ ಅವರ ಪರಿವಾರದಲ್ಲಿದ್ದ ಒಬ್ಬನು, “ನಾನು ಅದನ್ನು ಪರಿಹರಿಸುತ್ತೇನೆ” ಎಂದು ಉತ್ತರಿಸಿದನು. ಹೀಗೆ ಹೇಳುವ ಮೂಲಕ, ಶ್ರೀ ರಾಮನಿಗೆ ನಿಜವಾಗಿಯೂ ಬ್ರಹ್ಮಹತ್ಯಾ ದೋಷವುಂಟಾಗಿದೆ ಎಂದು ಅವನು ಭಾವಿಸಿದ್ದಾನೆ ಎಂಬುದು ವ್ಯಕ್ತವಾಯಿತು.",
          "ಆಗ ಶ್ರೀ ರಾಮನು ಹೀಗೆ ಹೇಳಿದನು:",
        ],
        secondQuote: [
          "ನನಗೆ ಬ್ರಹ್ಮಹತ್ಯಾ ದೋಷವುಂಟಾಗಿದೆ ಎಂದು ನೀನು ತಿಳಿದಿರುವುದರಿಂದ, ನೀನು ಬ್ರಹ್ಮರಾಕ್ಷಸನಾಗು.",
        ],
        closingParagraphs: [
          "ಅದೇ ಶಾಪವನ್ನು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ನಾರಾಯಣಾಚಾರ್ಯರಿಗೆ ನೀಡಿದರು.",
          "ಆದರೆ ನಾರಾಯಣಾಚಾರ್ಯರು ಸಾಮಾನ್ಯ ಜೀವಿಯಾಗಿರಲಿಲ್ಲ. ಅವರು ಭವಿಷ್ಯದಲ್ಲಿ ರುದ್ರ ಪದವಿಯನ್ನು ಹೊಂದಲಿರುವ ಭವಿರುದ್ರರಾಗುವವರಾಗಿದ್ದರು.",
          "ನಾರಾಯಣಾಚಾರ್ಯರು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರಿಗೆ ಶರಣಾಗಿ ಹೀಗೆ ಕೇಳಿದರು:",
        ],
        thirdQuote: ["ಸ್ವಾಮೀ, ನಾನು ಈ ಶಾಪದಿಂದ ಯಾವಾಗ ಮುಕ್ತನಾಗುತ್ತೇನೆ?"],
        finalParagraphs: ["ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಉತ್ತರಿಸಿದರು:"],
        fourthQuote: [
          "ನೀನು ಬ್ರಹ್ಮರಾಕ್ಷಸನಾದಾಗ ಎಲ್ಲರನ್ನೂ ಈ ಪ್ರಶ್ನೆಯನ್ನು ಕೇಳು: ‘ಆಕಾಮವೈ ಕೋ ನ ಸ್ನಾತಃ?’ ಈ ಪ್ರಶ್ನೆಗೆ ಸರಿಯಾದ ಉತ್ತರವನ್ನು ನೀಡುವವರು ನಿನ್ನನ್ನು ಈ ಶಾಪದಿಂದ ಮುಕ್ತಗೊಳಿಸುವರು.",
        ],
        tailParagraphs: [
          "ಅದರಂತೆ, ನಾರಾಯಣಾಚಾರ್ಯರು ಬ್ರಹ್ಮರಾಕ್ಷಸರಾಗಿ, ತಾವು ಭೇಟಿಯಾದ ಪ್ರತಿಯೊಬ್ಬರಿಗೂ ಈ ಪ್ರಶ್ನೆಯನ್ನು ಕೇಳತೊಡಗಿದರು. ಆದರೆ ಯಾರಿಗೂ ಸರಿಯಾದ ಉತ್ತರವನ್ನು ನೀಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ.",
          "ಕೊನೆಗೆ ಆ ಬ್ರಹ್ಮರಾಕ್ಷಸನು ಬದರೀಕ್ಷೇತ್ರಕ್ಕೆ ತಲುಪಿದನು. ಅಲ್ಲಿ ಅವನು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರನ್ನು ಭೇಟಿಯಾದನು. ತಮ್ಮ ಮೇಲೆ ಶಾಪವನ್ನು ನೀಡಿದ ಅದೇ ಗುರುಗಳು ಅವರೇ ಎಂಬುದನ್ನು ಗುರುತಿಸದೆ, ಬ್ರಹ್ಮರಾಕ್ಷಸನು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರಿಗೂ ಅದೇ ಪ್ರಶ್ನೆಯನ್ನು ಕೇಳಿದನು:",
        ],
        fifthQuote: ["ಆಕಾಮವೈ ಕೋ ನ ಸ್ನಾತಃ?"],
        answerIntro: ["ಆಗ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಅದಕ್ಕೆ ಉತ್ತರವನ್ನು ನೀಡಿದರು.", "ಅವರು ವಿವರಿಸಿದಂತೆ:"],
        bullets: [
          { prefix: "ಆ", middle: " ಎಂದರೆ ", suffix: "ಆಷಾಢ" },
          { prefix: "ಕಾ", middle: " ಎಂದರೆ ", suffix: "ಕಾರ್ತಿಕ" },
          { prefix: "ಮ", middle: " ಎಂದರೆ ", suffix: "ಮಾಘ" },
          { prefix: "ವೈ", middle: " ಎಂದರೆ ", suffix: "ವೈಶಾಖ" },
        ],
        answerParagraphs: [
          "ಈ ಮಾಸಗಳಲ್ಲಿ ಸ್ನಾನ ಮಾಡುವುದು ವಿಶೇಷ ಪುಣ್ಯಕರವೆಂದು ಪರಿಗಣಿಸಲಾಗುತ್ತದೆ. ಆದರೆ ಬ್ರಹ್ಮರಾಕ್ಷಸನಾಗಿದ್ದ ನಾರಾಯಣಾಚಾರ್ಯರು ಈ ಪವಿತ್ರ ಮಾಸಗಳಲ್ಲಿ ಅಂತಹ ಸ್ನಾನವನ್ನು ಮಾಡಿರಲಿಲ್ಲ.",
          "ಆದ್ದರಿಂದ “ಆಕಾಮವೈ ಕೋ ನ ಸ್ನಾತಃ?” ಎಂಬುದರ ಅರ್ಥ:",
          "“ಈ ಮಾಸಗಳಲ್ಲಿ ಸ್ನಾನ ಮಾಡದವರು ಯಾರು?”",
          "ಎಂಬುದಾಗಿದೆ.",
          "ಅದಕ್ಕೆ ಉತ್ತರ:",
          "“ತ್ವಂ ನ ಸ್ನಾತಃ” - “ನೀನು ಸ್ನಾನ ಮಾಡಿಲ್ಲ.”",
          "ಎಂದು ನೀಡಲಾಯಿತು.",
        ],
        finalTail: [
          "ಸರಿಯಾದ ಉತ್ತರವನ್ನು ನೀಡಿದ ತಕ್ಷಣವೇ ನಾರಾಯಣಾಚಾರ್ಯರು ಶಾಪದಿಂದ ಮುಕ್ತರಾದರು.",
          "ಆ ದಿನದಿಂದ ಅವರು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಪರಮ ಭಕ್ತ ಸೇವಕರಾಗಿ, ಅವರ ಪಲ್ಲಕ್ಕಿಯನ್ನು ಹೊತ್ತುಕೊಂಡು ಸೇವೆ ಸಲ್ಲಿಸಿದರು. ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಅವರನ್ನು ಪ್ರೀತಿಯಿಂದ ಭೂತರಾಜ ಎಂದು ಕರೆದರು. ಇದರ ಅರ್ಥ ಭೂತಗಣಗಳ ರಾಜ ಎಂಬುದಾಗಿದೆ.",
          "ಹೀಗೆ ಭೂತರಾಜರಿಗೆ ಮತ್ತು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರಿಗೆ ನಡುವಿನ ಪವಿತ್ರ ಸಂಬಂಧವು ಆರಂಭವಾಯಿತು.",
        ],
      },
      {
        title: "ಸೋದೆಯಲ್ಲಿ ಭೂತರಾಜರು",
        paragraphs: [
          "ಇಂದಿಗೂ ಸೋದೆಗೆ ಯಾತ್ರೆ ಕೈಗೊಳ್ಳುವ ಭಕ್ತರು, ತಮ್ಮ ಯಾತ್ರೆಯನ್ನು ಮುಂದುವರಿಸುವ ಮೊದಲು ಸಂಪ್ರದಾಯದಂತೆ ಭೂತರಾಜರ ದರ್ಶನವನ್ನು ಪಡೆಯುತ್ತಾರೆ. ಭಕ್ತರು ಭೂತರಾಜರ ಮುಂದೆ ಎರಡು ತೆಂಗಿನಕಾಯಿಗಳನ್ನು ಸಮರ್ಪಿಸಿ ನಂತರ ತಮ್ಮ ಯಾತ್ರೆಯನ್ನು ಮುಂದುವರಿಸುತ್ತಾರೆ.",
          "ಈ ಎರಡು ತೆಂಗಿನಕಾಯಿಗಳು ಸಾಂಪ್ರದಾಯಿಕವಾಗಿ ಈ ಕೆಳಗಿನವುಗಳನ್ನು ಸಂಕೇತಿಸುತ್ತವೆ:",
        ],
        bullets: [
          "ಇಷ್ಟಪ್ರಾಪ್ತಿ - ಅಪೇಕ್ಷಿತವಾದುದರ ಪ್ರಾಪ್ತಿ",
          "ಅನಿಷ್ಟನಿವಾರಣೆ - ಅನಪೇಕ್ಷಿತವಾದುದರ ನಿವಾರಣೆ",
        ],
        paragraphsAfter: [
          "ಭೂತರಾಜರನ್ನು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಪರಮ ಭಕ್ತ ಸೇವಕರಾಗಿ ಹಾಗೂ ಭಕ್ತರನ್ನು ರಕ್ಷಿಸುವ, ಅವರ ಅಡೆತಡೆಗಳನ್ನು ನಿವಾರಿಸುವ ರಕ್ಷಕರಾಗಿ ಪೂಜಿಸಲಾಗುತ್ತದೆ.",
          "ಭೂತರಾಜರಿಗೆ ಸಲ್ಲಿಸುವ ಸಾಂಪ್ರದಾಯಿಕ ಪ್ರಾರ್ಥನೆ:",
        ],
        prayer: [
          "ಭೂತರಾಜ ನಮಸ್ತುಭ್ಯಂ",
          "ವಾದಿರಾಜಗುರೋಃ ಪ್ರಿಯ",
          "ವಿದ್ರಾವಯ ಸರ್ವಬಾಧಾಂ",
          "ನಃ ಸದಾ ತ್ವಂ ರಕ್ಷಣಂ ಕುರು.",
        ],
        meaningTitle: "ಅರ್ಥ",
        meaning: [
          "ಓ ಭೂತರಾಜರೇ, ನಿಮಗೆ ನನ್ನ ನಮಸ್ಕಾರಗಳು.",
          "ಗುರು ವಾದಿರಾಜರಿಗೆ ಪ್ರಿಯರಾದ ಸೇವಕರೇ,",
          "ಎಲ್ಲಾ ಸಂಕಷ್ಟಗಳು ಮತ್ತು ಅಡೆತಡೆಗಳನ್ನು ದೂರಮಾಡಿ,",
          "ಸದಾ ನಮ್ಮನ್ನು ರಕ್ಷಿಸಿರಿ.",
        ],
      },
    ],
  },
};

const renderParagraphs = (paragraphs = []) =>
  paragraphs.map((paragraph) => (
    <p key={paragraph} className="text-[17px] leading-relaxed text-[#3B2A1A]/85" style={{ textAlign: "justify" }}>
      {paragraph}
    </p>
  ));

const QuoteBlock = ({ lines = [] }) => (
  <blockquote className="rounded-2xl border-l-4 border-[#D4AF37] bg-[#F2E7D5]/55 px-5 py-4 font-serif-display text-xl text-[#6D3D16] leading-relaxed">
    {lines.map((line) => (
      <span key={line} className="block">
        “{line}”
      </span>
    ))}
  </blockquote>
);

const PrayerBlock = ({ lines = [] }) => (
  <blockquote className="rounded-3xl border border-[#D4AF37]/40 bg-[#FBF7F0] p-6 sm:p-8 text-center shadow-inner">
    {lines.map((line) => (
      <span key={line} className="block font-serif-display text-2xl text-[#6D3D16] leading-relaxed">
        {line}
      </span>
    ))}
  </blockquote>
);

const ContentSection = ({ section, index }) => {
  const isAccent = index % 2 === 1;

  return (
    <section className={`py-14 ${isAccent ? "bg-[#F2E7D5]/40 border-y border-[#D4AF37]/20" : ""}`}>
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        <SectionHeading title={section.title} align="center" />
        <div className="mt-9 space-y-5">
          {renderParagraphs(section.paragraphs)}
          {section.quote && <QuoteBlock lines={section.quote} />}
          {renderParagraphs(section.paragraphsAfter)}
          {section.secondQuote && <QuoteBlock lines={section.secondQuote} />}
          {renderParagraphs(section.closingParagraphs)}
          {section.thirdQuote && <QuoteBlock lines={section.thirdQuote} />}
          {renderParagraphs(section.finalParagraphs)}
          {section.fourthQuote && <QuoteBlock lines={section.fourthQuote} />}
          {renderParagraphs(section.tailParagraphs)}
          {section.fifthQuote && <QuoteBlock lines={section.fifthQuote} />}
          {renderParagraphs(section.answerIntro)}
          {section.bullets && (
            <ul className="space-y-2 rounded-2xl border border-[#D4AF37]/30 bg-[#FBF7F0]/80 p-5 text-[#3B2A1A]/85">
              {section.bullets.map((item) => {
                const key = typeof item === "string" ? item : `${item.prefix}-${item.suffix}`;

                return (
                  <li key={key} className="flex gap-3 text-[17px] leading-relaxed">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#B65A14]" />
                    <span>
                      {typeof item === "string" ? (
                        item
                      ) : (
                        <>
                          <strong className="font-bold text-[#3B2A1A]">{item.prefix}</strong>
                          {item.middle}
                          <strong className="font-bold text-[#3B2A1A]">{item.suffix}</strong>
                        </>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {renderParagraphs(section.answerParagraphs)}
          {section.sixthQuote && <QuoteBlock lines={section.sixthQuote} />}
          {renderParagraphs(section.answerTail)}
          {section.seventhQuote && <QuoteBlock lines={section.seventhQuote} />}
          {renderParagraphs(section.finalTail)}
          {section.prayer && <PrayerBlock lines={section.prayer} />}
          {section.meaningTitle && (
            <h3 className="pt-2 font-serif-display text-3xl text-[#3B2A1A]">{section.meaningTitle}</h3>
          )}
          {section.meaning && <PrayerBlock lines={section.meaning} />}
        </div>
      </div>
    </section>
  );
};

const Bhootharaja = () => {
  const { lang } = useLang();
  const copy = CONTENT[lang] || CONTENT.en;

  return (
    <PageLayout>
      <PageHero
        eyebrow={copy.heroEye}
        title={copy.heroTitle}
        subtitle={copy.heroSubtitle}
      />

      <section className="pt-16 pb-10" data-testid="bhootharaja-intro">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="relative lg:col-span-12 flex justify-center lg:justify-center">
            <div className="w-full max-w-[500px] lg:max-w-[450px] aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl border-4 border-[#D4AF37]/40 ring-1 ring-[#B65A14]/20 bg-[#F2E7D5]">
              <img
                src={BHOOTHARAJA_IMG}
                alt={copy.heroTitle}
                className="w-full h-full object-contain"
                data-testid="bhootharaja-hero-image"
              />
            </div>
            <div className="absolute -top-8 -left-8 opacity-60 pointer-events-none">
              <Mandala size={130} color="#D4AF37" opacity={0.7} />
            </div>
            <div className="absolute -bottom-8 -right-8 opacity-60 pointer-events-none">
              <Mandala size={130} color="#B65A14" opacity={0.5} />
            </div>
          </div>
          {/* <div className="lg:col-span-7 lg:pt-4">
            <div className="overline mb-3 text-[#B65A14]">{copy.heroEye}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3B2A1A] leading-tight mb-5">
              {copy.sections[0].title}
            </h2>
            <div className="gold-underline mb-6" />
            <p className="text-lg leading-relaxed text-[#3B2A1A]/90" style={{ textAlign: "justify" }}>
              {copy.sections[0].paragraphs[0]}
            </p>
          </div> */}
        </div>
      </section>

      <SectionDivider variant="lotus" />

      {copy.sections.map((section, index) => (
        <ContentSection key={section.title} section={section} index={index} />
      ))}

      <section className="py-16 relative overflow-hidden bg-gradient-to-b from-transparent to-[#F2E7D5]/60" data-testid="bhootharaja-closing">
        <div className="absolute inset-x-0 top-0 flex justify-center opacity-40 pointer-events-none">
          <Mandala size={220} color="#D4AF37" opacity={0.6} />
        </div>
        <div className="max-w-3xl mx-auto px-4 lg:px-8 relative text-center">
          <div className="mt-6">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-8 py-4 text-sm font-semibold hover:bg-[#8F4610] transition shadow-lg"
              data-testid="bhootharaja-cta-visit"
            >
              {copy.ctaBtn} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Bhootharaja;
